import { StepRun } from "@/models/flows";
import { useQuery } from "react-query";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { StatusBadge } from "../../StatusBadge";
import { ViewResponseDialog } from "@/components/dashboard/forms/ViewResponseDialog";
import { useState } from "react";
import { SquareArrowOutUpRight } from "lucide-react";

interface Props {
  stepRun: StepRun;
}

export const SendFormAndWait = ({ stepRun }: Props) => {
  const formId: number = stepRun.step.config.formId;
  const formResponseId: number | undefined = stepRun.outputData?.formResponseId;
  const [viewResponseOpen, setViewResponseOpen] = useState(false);

  const {
    data: form,
    isLoading,
    isError,
  } = useQuery(
    ["form", formId],
    async () => {
      const response = await axios.get(`${BASE_URL}/dashboard/forms/${formId}`);
      return response.data;
    },
    {
      enabled: !!formId, // Only fetch if formId exists
    }
  );

  const {
    data: formResponse,
    isLoading: formResponseIsLoading,
    isError: formResponseError,
  } = useQuery(
    ["formResponse", formResponseId],
    async () => {
      const response = await axios.get(
        `${BASE_URL}/dashboard/forms/${formId}/responses/${formResponseId}`
      );
      return response.data;
    },
    {
      enabled: !!formResponseId, // Only fetch if formId exists
    }
  );

  if (isLoading) {
    return <div>Loading form details...</div>;
  }

  if (isError) {
    return <div>Failed to load form details.</div>;
  }

  let totalCorrectAnswers = 0;

  if (form.mode.id === "quiz") {
    totalCorrectAnswers = form.fields
      .filter(
        (field: any) =>
          field.fieldType === "multipleChoice" || field.fieldType === "dropdown"
      )
      .reduce((total: number, field: any) => {
        const correctOptions = field.options.filter(
          (option: any) => option.isCorrect
        );
        return total + correctOptions.length;
      }, 0);
  }

  return (
    <div>
      <p className="dark:text-white">{form.name}</p>
      {formResponse && (
        <div className="-mt-0.5">
          <ViewResponseDialog
            open={viewResponseOpen}
            setOpen={setViewResponseOpen}
            form={form}
            formResponse={formResponse}
          />
          <button
            id="form-responses"
            onClick={() => setViewResponseOpen(true)}
            className="flex mt-3 items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20"
          >
            <div className="">View this response</div>
          </button>
        </div>
      )}
      <div className="mt-4">
        <StatusBadge
          status={stepRun.status}
          statusOverride={
            stepRun.status === "waiting" ? "Waiting on completion" : undefined
          }
        />
      </div>
      <div className="w-20 border-t border-gray-400/20 mt-4 mb-1"></div>

      <div className="w-full">
        {form.mode.id === "quiz" && stepRun.outputData?.score && (
          <div
            onClick={() => setViewResponseOpen(true)}
            className="inline-flex mt-3 items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20 cursor-pointer"
          >
            <div>
              Score: {stepRun.outputData.score} / {totalCorrectAnswers}
            </div>
          </div>
        )}
      </div>
      <div className="w-full">
        {stepRun.outputData?.ending && (
          <>
            {stepRun.outputData.ending.redirectUrl ? (
              <div
                className="inline-flex mt-3 items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20 cursor-pointer"
                onClick={() =>
                  window.open(stepRun.outputData.ending.redirectUrl, "_blank")
                }
              >
                <div className="flex items-center justify-center">
                  Was redirected to: {stepRun.outputData.ending.redirectUrl}
                  <SquareArrowOutUpRight className="ml-2 h-3 w-3 text-gray-400" />
                </div>
              </div>
            ) : (
              <div className="mt-3">
                {stepRun.outputData.ending.content && (
                  <div>
                    <div className="dark:text-zinc-400">Ending content</div>
                    <div
                      className="prose dark:prose-invert mt-3 border dark:border-white/5 dark:bg-zinc-800 w-full rounded-md p-4"
                      dangerouslySetInnerHTML={{
                        __html: stepRun.outputData.ending.content,
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
